'use client';

import Icon from '@/components/ui/AppIcon';

interface Recommendation {
  partNumber: string;
  partName: string;
  keywords: string[];
  compatibility: string;
  estimatedPrice: string;
}

interface AIRecommendationsProps {
  recommendations: Recommendation[];
  onKeywordClick: (keyword: string) => void;
}

const AIRecommendations = ({ recommendations, onKeywordClick }: AIRecommendationsProps) => {
  if (recommendations.length === 0) {
    return null;
  }

  return (
    <div className="bg-card rounded-xl shadow-elevation-md p-6 space-y-6">
      <div className="flex items-start space-x-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent/10 flex-shrink-0">
          <Icon name="SparklesIcon" size={24} className="text-accent" />
        </div>
        <div className="flex-1">
          <h3 className="font-heading text-lg font-semibold text-text-primary mb-1">
            AI-Powered Recommendations
          </h3>
          <p className="font-body text-sm text-text-secondary">
            Based on your vehicle details, here are the suggested OEM parts and search keywords
          </p>
        </div>
      </div>

      <div className="space-y-4">
        {recommendations.map((rec, index) => (
          <div key={index} className="rounded-lg border border-border bg-muted/50 p-4 space-y-3">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h4 className="font-heading text-base font-semibold text-text-primary mb-1">
                  {rec.partName}
                </h4>
                <p className="font-mono text-sm text-primary">
                  Part #: {rec.partNumber}
                </p>
              </div>
              <div className="text-right">
                <p className="font-caption text-xs text-text-secondary mb-1">Est. Price</p>
                <p className="font-heading text-lg font-semibold text-accent">
                  {rec.estimatedPrice}
                </p>
              </div>
            </div>

            <div>
              <p className="font-caption text-xs font-medium text-text-secondary mb-2">
                Compatibility
              </p>
              <p className="font-body text-sm text-text-primary">
                {rec.compatibility}
              </p>
            </div>

            <div>
              <p className="font-caption text-xs font-medium text-text-secondary mb-2">
                Search Keywords
              </p>
              <div className="flex flex-wrap gap-2">
                {rec.keywords.map((keyword, idx) => (
                  <button
                    key={idx}
                    onClick={() => onKeywordClick(keyword)}
                    className="inline-flex items-center space-x-1 rounded-md bg-primary/10 px-3 py-1.5 font-body text-xs font-medium text-primary transition-smooth hover:bg-primary hover:text-primary-foreground active:scale-95"
                  >
                    <Icon name="MagnifyingGlassIcon" size={14} />
                    <span>{keyword}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-lg bg-accent/10 border border-accent/20 p-4">
        <div className="flex items-start space-x-3">
          <Icon name="LightBulbIcon" size={20} className="text-accent flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-caption text-xs font-medium text-accent mb-1">
              Pro Tip
            </p>
            <p className="font-body text-sm text-text-primary">
              Click on any keyword to search eBay marketplace instantly. Use multiple keywords for better results.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIRecommendations;